// src/components/OccupiedCapacityChart.js
import React, {useState} from 'react';
import {
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ComposedChart,
    Line
} from 'recharts';
import {FormControlLabel, Switch, Typography} from '@mui/material';

// Helper: Format a given date (YYYY-MM-DD) and hour into "DD/M hh:mm AM/PM"
const formatHourLabel = (dateStr, hour) => {
    const dateTimeStr = `${dateStr}T${hour.toString().padStart(2, '0')}:00:00Z`;
    const d = new Date(dateTimeStr);
    if (isNaN(d)) return '';
    const options = {day: 'numeric', month: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true};
    return d.toLocaleString('en-US', options);
};

// Helper: Format a given date (YYYY-MM-DD) into "DD/M"
const formatDayLabel = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const day = d.getUTCDate();
    const month = d.getUTCMonth() + 1;
    return `${day}/${month}`;
};

// Helper: Determine if a given date (YYYY-MM-DD) is a weekend.
const isWeekend = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getUTCDay();
    return day === 0 || day === 6;
};

// Compute effective player-seconds for a session (players × min(actualDuration, plannedDuration))
const computeEffectiveSeconds = (item) => {
    const gameStart = new Date(item.attributes.gameStartDate);
    const gameEnd = new Date(item.attributes.gameEndDate);
    if (isNaN(gameStart) || isNaN(gameEnd)) return 0;
    const actual = (gameEnd - gameStart) / 1000;
    const planned = item.attributes.game_variant &&
    item.attributes.game_variant.data &&
    item.attributes.game_variant.data.attributes &&
    item.attributes.game_variant.data.attributes.duration
        ? item.attributes.game_variant.data.attributes.duration
        : actual;
    const effective = planned ? Math.min(actual, planned) : actual;
    const players = item.attributes.score ?? 0;
    return players * effective;
};

// This unified function groups rawData based on the grouping flag ("hour" or "day").
// For "hour": groups by date and hour (only sessions in working period).
// For "day": groups by date (only sessions that occur during working hours) and uses dayCapacity = maxPlayers × workingHours × 3600.
const calculateOccupiedData = (rawData, grouping, workingStart, workingEnd, maxPlayers) => {
    const groups = {};
    rawData.forEach(item => {
        const startStr = item.attributes.gameStartDate;
        const endStr = item.attributes.gameEndDate;
        if (!startStr || !endStr) return;
        const d = new Date(startStr);
        if (isNaN(d)) return;
        const date = d.toISOString().split('T')[0]; // "YYYY-MM-DD"
        const hour = d.getUTCHours();
        // Only consider sessions within the working period:
        if (hour < workingStart || hour >= workingEnd) return;
        // For grouping:
        const key = grouping === 'hour' ? `${date}_${hour}` : date;
        if (!groups[key]) {
            groups[key] = {
                date,
                // For hourly grouping, store hour; for day grouping, we’ll compute average occupancy later.
                hours: grouping === 'hour' ? [hour] : [hour],
                effectiveTotal: 0,
                counts: {},
                totalPlayers: 0  // accumulate total players across sessions in this group
            };
            for (let i = 0; i <= maxPlayers; i++) {
                groups[key].counts[i] = 0;
            }
        }
        groups[key].effectiveTotal += computeEffectiveSeconds(item);
        const players = Number(item.attributes.score) || 0;
        groups[key].totalPlayers += players;
        const cat = players > maxPlayers ? maxPlayers : players;
        groups[key].counts[cat] += 1;
        if (grouping === 'day') {
            groups[key].hours.push(hour);
        }
    });
    // Now build chart data.
    const chartData = [];
    Object.values(groups).forEach(group => {
        let capacity;
        let label;
        if (group.hours.length === 0) return;
        if (group.hours.length === 1) {
            // Hourly grouping: capacity is maxPlayers * 3600.
            capacity = maxPlayers * 3600;
            label = formatHourLabel(group.date, group.hours[0]);
        } else {
            // For daily grouping, assume capacity = maxPlayers * (workingEnd - workingStart) * 3600.
            const workingHours = workingEnd - workingStart;
            capacity = maxPlayers * workingHours * 3600;
            label = formatDayLabel(group.date);
        }
        const occupancy = capacity > 0 ? (group.effectiveTotal / capacity) * 100 : 0;
        // Total sessions in group.
        const totalSessions = Object.values(group.counts).reduce((sum, cnt) => sum + cnt, 0);
        const breakdown = {};
        for (let i = 0; i <= maxPlayers; i++) {
            const fraction = totalSessions > 0 ? group.counts[i] / totalSessions : 0;
            breakdown[i] = occupancy * fraction;
        }
        chartData.push({
            label,
            occupancy: +occupancy.toFixed(2),
            ...breakdown,
            counts: group.counts,
            totalSessions,
            totalPlayers: group.totalPlayers,
            date: group.date,
            // For hourly grouping, include hour; for daily, we might average hours but it's not used in label.
            hour: grouping === 'hour' ? group.hours[0] : null,
        });
    });
    // Sorting:
    if (grouping === 'hour') {
        chartData.sort((a, b) => {
            if (a.date === b.date) return a.hour - b.hour;
            return new Date(a.date) - new Date(b.date);
        });
    } else {
        chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return chartData;
};

// Helper: Generate color for a given category.
// If weekend is true, use blue shades; else, green shades.
const getColorForCategory = (category, maxPlayers, weekend) => {
    const factor = category / maxPlayers;
    if (weekend) {
        const r = 173 - Math.round(factor * 173);
        const g = 216 - Math.round(factor * 216);
        const b = 230 - Math.round(factor * (230 - 139));
        return `rgb(${r},${g},${b})`;
    } else {
        const r = 144 - Math.round(factor * 144);
        const g = 238 - Math.round(factor * (238 - 100));
        const b = 144 - Math.round(factor * 144);
        return `rgb(${r},${g},${b})`;
    }
};

// Custom Tooltip: Display the label, total games, and percentages for each category.
const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const totalGames = data.totalSessions;
        const totalPlayers = data.totalPlayers;
        return (
            <div style={{backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc'}}>
                <p style={{margin: '0px'}}><strong>{`Time: ${label}`}</strong></p>
                <p style={{margin: '0px'}}>{`Total Games Played: ${totalGames}`}</p>
                <p style={{margin: '0px'}}>{`Total Players: ${totalPlayers}`}</p>
                {Object.keys(data.counts).sort((a, b) => b - a).map((cat) => {
                    const count = data.counts[cat];
                    const pct = totalGames > 0 ? ((count / totalGames) * 100).toFixed(0) : 0;
                    return <p style={{margin: '0px'}} key={cat}>{`${cat} players: ${pct}%`}</p>;
                })}
                <p style={{margin: '0px'}}>{`Occupancy: ${data.occupancy.toFixed(2)}%`}</p>
            </div>
        );
    }
    return null;
};

const OccupiedCapacityChart = ({rawData, grouping, workingStart, workingEnd, maxPlayers}) => {
    const [showPlayersAxis, setShowPlayersAxis] = useState(false);
    const chartData = calculateOccupiedData(rawData, grouping, workingStart, workingEnd, maxPlayers);
    // For each data point, determine weekend flag based on its date.
    const dataWithWeekend = chartData.map(d => ({...d, weekend: isWeekend(d.date)}));
    // Determine maximum totalPlayers for right Y-axis domain.
    // const maxPlayersValue = dataWithWeekend.reduce((max, d) => Math.max(max, d.totalPlayers), 0);
    return (
        <div>
            <Typography variant="h5" align="center" gutterBottom>
                {grouping === 'hour'
                    ? 'Actual Capacity/Hour (Stacked by Player Count)'
                    : 'Actual Capacity/Day (Stacked by Player Count)'}
            </Typography>
            <FormControlLabel className="no-print"
                control={
                    <Switch
                        checked={showPlayersAxis}
                        onChange={() => setShowPlayersAxis(!showPlayersAxis)}
                        color="primary"
                    />
                }
                label="Show Players Axis"
            />
            <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={dataWithWeekend} margin={{top: 20, right: 50, left: 20, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="label"/>
                    <YAxis tickFormatter={(val) => `${val.toFixed(1)}%`}/>
                    {showPlayersAxis && (
                        <YAxis
                            orientation="right"
                            yAxisId="right"
                        />
                    )}
                    <Tooltip content={<CustomTooltip/>}/>
                    {/* Render stacked bars for each category from maxPlayers down to 0 */}
                    {Array.from({length: maxPlayers + 1}, (_, i) => maxPlayers - i).map((cat) => (
                        <Bar key={cat} dataKey={cat.toString()} stackId="a">
                            {dataWithWeekend.map((entry, index) => {
                                const color = getColorForCategory(cat, maxPlayers, entry.weekend);
                                return <Cell key={`cell-${index}`} fill={color}/>;
                            })}
                        </Bar>
                    ))}
                    {showPlayersAxis && (
                        <Line
                            type="monotone"
                            dataKey="totalPlayers"
                            stroke="#ff7300"
                            strokeWidth={2}
                            dot={{r: 4}}
                            yAxisId="right"
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default OccupiedCapacityChart;
